import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { Button } from '../Button';
import {
  useCreateEndpoint,
  useUpdateEndpoint,
  type Endpoint,
} from '../../lib/queries/storage-endpoints';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  endpointUrl: z.string().url('Must be a URL'),
  bucket: z.string().min(1, 'Required'),
  accessKeyId: z.string().min(1, 'Required'),
  secretAccessKey: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

// Kept bg-white/4 (not InviteMemberForm's bg-bg-elevated) so inputs keep an
// edge on the elevated card the inline form renders inside.
const inputCls =
  'w-full rounded border border-white/10 bg-white/4 px-3 py-2 text-sm text-primary placeholder:text-primary/20 focus:border-[--color-accent] focus:outline-none';

export default function EndpointForm({
  onCloseModal,
  onSuccess,
  endpoint,
  inline = false,
}: {
  onCloseModal?: () => void;
  /** Fired after a successful save. Separate from onCloseModal so an
      always-visible form can reset itself without offering a Cancel. */
  onSuccess?: () => void;
  endpoint?: Endpoint;
  /** Rendered inside an expanded endpoint card rather than a modal. */
  inline?: boolean;
}) {
  const [showSecret, setShowSecret] = useState<boolean>(false);
  const create = useCreateEndpoint();
  const update = useUpdateEndpoint();
  const isPending = create.isPending || update.isPending;
  const isEdit = Boolean(endpoint);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: endpoint
      ? {
          name: endpoint.name,
          endpointUrl: endpoint.endpointUrl,
          bucket: endpoint.bucket,
          accessKeyId: endpoint.accessKeyId,
          secretAccessKey: '',
        }
      : undefined,
  });

  function onSubmit(v: FormValues) {
    // Empty secret on edit means "keep the stored one"; on create it's required.
    const secretAccessKey = v.secretAccessKey?.trim() || undefined;
    if (!isEdit && !secretAccessKey) {
      setError('secretAccessKey', { message: 'Required' });
      return;
    }
    const payload = { ...v, secretAccessKey };
    const done = () => {
      onSuccess?.();
      onCloseModal?.();
    };
    if (endpoint) {
      update.mutate({ id: endpoint.id, ...payload }, { onSuccess: done });
    } else {
      create.mutate(payload, { onSuccess: done });
    }
  }

  const mutationError = create.isError || update.isError;
  const errorMessage = ((create.error ?? update.error) as Error | null)
    ?.message;

  const fields = (
    <>
      <Field label="Name" error={errors.name?.message}>
        <input
          {...register('name')}
          placeholder="Production R2"
          className={inputCls}
        />
      </Field>
      <Field label="Endpoint URL" error={errors.endpointUrl?.message}>
        <input
          {...register('endpointUrl')}
          placeholder="https://<account>.r2.cloudflarestorage.com"
          className={inputCls}
        />
      </Field>
      <Field label="Bucket" error={errors.bucket?.message}>
        <input {...register('bucket')} className={inputCls} />
      </Field>
      <Field label="Access Key ID" error={errors.accessKeyId?.message}>
        <input {...register('accessKeyId')} className={inputCls} />
      </Field>
      <Field label="Access Key Secret" error={errors.secretAccessKey?.message}>
        <div className="relative flex items-center gap-2">
          <input
            {...register('secretAccessKey')}
            type={showSecret ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={isEdit ? 'Leave blank to keep current secret' : ''}
            className={`${inputCls} flex-1`}
          />
          <button
            type="button"
            onClick={() => setShowSecret((v) => !v)}
            aria-pressed={showSecret}
            className="text-primary absolute top-1/2 right-1 shrink-0 -translate-y-1/2 rounded-sm border border-white/15 px-4 py-1.5 text-xs transition-colors hover:border-white/30"
          >
            {showSecret ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="text-subtle mt-2 text-xs">
          Stored encrypted at rest. Never displayed after save.
        </p>
      </Field>
    </>
  );

  // Inline: embedded in an endpoint card, so no modal chrome — compact grid.
  if (inline) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-4">
        <div className="grid gap-4 md:grid-cols-2">{fields}</div>

        <p className="text-subtle text-[0.625rem]">
          The secret is encrypted at rest and never returned by the API.
        </p>

        <div className="flex items-center justify-end gap-3 pt-1">
          {mutationError && (
            <p className="text-danger mr-auto text-xs">{errorMessage}</p>
          )}
          {update.isSuccess && !isDirty && (
            <p className="text-success mr-auto text-xs">Saved</p>
          )}
          {/* Inline editing has nothing to dismiss; inline *creation* passes a
              dismiss handler, so honour it whenever one is supplied. */}
          {onCloseModal && (
            <Button type="button" variant="secondary" onClick={onCloseModal}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isPending || !isDirty}>
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
          </Button>
        </div>
      </form>
    );
  }

  // Modal: sectioned chrome mirroring InviteMemberForm.
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="flex items-center justify-between border-b border-white/8 px-6 py-4">
        <h2 className="text-primary text-base font-medium">
          {isEdit ? 'Edit S3 endpoint' : 'Add S3 endpoint'}
        </h2>
        <button
          type="button"
          onClick={onCloseModal}
          className="text-muted hover:text-primary transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4 px-6 py-5">
        {fields}

        {mutationError && <p className="text-danger text-sm">{errorMessage}</p>}
      </div>

      <div className="border-primary/8 flex justify-end gap-3 border-t px-6 py-4">
        <Button type="button" variant="secondary" onClick={onCloseModal}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-muted mb-1 block text-xs font-medium">
        {label}
      </label>
      {children}
      {error && <p className="text-danger mt-1 text-xs">{error}</p>}
    </div>
  );
}
